import CopyIcon from '@mui/icons-material/CopyAll';
import { Box, Button, InputAdornment, Skeleton, Tab, Tabs, TextField, Tooltip } from '@mui/material';
import { ReactNode, Suspense, createContext, lazy, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../modal';
import { VideoOptions } from './types';

const Video = lazy(() => import('./Video'));

export type LabeledVideoOptions = VideoOptions & {
  label: string;
};

type PlayerContextValue = {
  play: (source: VideoPlayerSource) => void;
};

const defaultValue = {
  play: () => undefined,
};

export type VideoPlayerSource = LabeledVideoOptions | LabeledVideoOptions[];

export const VideoPlayerContext = createContext<PlayerContextValue>(defaultValue);

export const VideoPlayerProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  let contextValue: PlayerContextValue = defaultValue;
  const [modalContext, setModalContext] = useState<LabeledVideoOptions[]>();
  const [videoData, setVideoData] = useState<Pick<VideoOptions, 'source'> & Partial<VideoOptions>>();
  const clearContext = () => {
    setModalContext(undefined);
    setVideoData(undefined);
  };

  contextValue = {
    play: (options: VideoPlayerSource) => {
      const newContext = Array.isArray(options) ? options : [options];
      setModalContext(newContext);
      if (newContext.length > 0) {
        setVideoData(newContext[0]);
      }
    },
  };
  const videoUrl = `${videoData?.source?.startsWith('http') ? '' : location.origin}${videoData?.source}`;
  return (
    <VideoPlayerContext.Provider value={contextValue}>
      <>
        <Modal
          open={!!modalContext}
          onClose={clearContext}
          maxWidth="lg"
          actions={
            <Button variant="outlined" color="secondary" onClick={clearContext}>
              {t('button.close')}
            </Button>
          }
          titleElement={
            modalContext && modalContext?.length > 1 ? (
              <Tabs
                value={modalContext.findIndex(({ source }) => source === videoData?.source)}
                indicatorColor="secondary"
                onChange={(_, index) => modalContext.length > index && setVideoData(modalContext[index])}
              >
                {modalContext.map(({ label }, index) => (
                  <Tab
                    key={label + index}
                    label={label}
                    value={index}
                    sx={theme => ({
                      background: theme.palette.background.default,
                      '&&&.MuiButtonBase-root': { margin: 0, padding: '0 16px' },
                    })}
                  />
                ))}
              </Tabs>
            ) : undefined
          }
          sx={{
            '.MuiIconButton-root': {
              marginBottom: '16px',
            },
            '&& .MuiDialogTitle-root': {
              margin: '32px 0 0',
              alignItems: 'flex-end',
            },
          }}
        >
          {modalContext && (
            <Box display="flex" flexDirection="column" height="100%" width="100%">
              <Box display="flex" flexDirection="column" padding="16px" gap="8px" bgcolor="background.default">
                {videoData?.source && (
                  <Suspense fallback={<Skeleton width="100%" height={480} variant="rounded" />}>
                    <Video key={videoData.source} {...videoData} autoPlay showStats />
                  </Suspense>
                )}
                <TextField
                  value={videoUrl}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={t('button.copyToClipboard')}>
                          <CopyIcon
                            onClick={() => navigator.clipboard.writeText(videoUrl)}
                            sx={{ cursor: 'pointer' }}
                          />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Box>
          )}
        </Modal>
        {children}
      </>
    </VideoPlayerContext.Provider>
  );
};

export const useVideoPlayerContext = () => useContext(VideoPlayerContext);
